'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { TurnstileInstance } from '@marsidev/react-turnstile';

import { CaptchaField } from './captcha-field';

const CAPTCHA_TIMEOUT_MS = 10000;

/**
 * @name useCaptcha
 * @description Zero-boilerplate hook for captcha integration.
 * Manages token state and instance internally, exposing a clean API.
 * Logs an error if captcha fails to resolve within 10 seconds (for Sentry tracking).
 *
 * @example
 * ```tsx
 * function SignInForm({ captchaSiteKey }) {
 *   const captcha = useCaptcha({ siteKey: captchaSiteKey });
 *
 *   const handleSubmit = async (data) => {
 *     await signIn({ ...data, captchaToken: captcha.token });
 *     captcha.reset();
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       {captcha.field}
 *       <button>Submit</button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useCaptcha(
  { siteKey, nonce }: { siteKey?: string; nonce?: string } = {
    siteKey: undefined,
    nonce: undefined,
  },
) {
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const instanceRef = useRef<TurnstileInstance | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutLoggedRef = useRef(false);

  const reset = useCallback(() => {
    instanceRef.current?.reset();
    setToken('');
    setError(null);
    timeoutLoggedRef.current = false;
  }, []);

  const handleTokenChange = useCallback((newToken: string) => {
    setToken(newToken);
  }, []);

  const handleInstanceChange = useCallback(
    (instance: TurnstileInstance | null) => {
      instanceRef.current = instance;
    },
    [],
  );

  const handleError = useCallback((errorCode: string) => {
    setError(errorCode);
  }, []);

  const field = useMemo(
    () => (
      <CaptchaField
        siteKey={siteKey}
        onTokenChange={handleTokenChange}
        onInstanceChange={handleInstanceChange}
        onError={handleError}
        nonce={nonce}
      />
    ),
    [siteKey, nonce, handleTokenChange, handleInstanceChange, handleError],
  );

  // Ready when captcha is not configured (no siteKey) or token is available
  const isReady = !siteKey || token !== '';

  // Track captcha timeout for error logging (Sentry will capture console.error)
  useEffect(() => {
    if (!siteKey) {
      return;
    }

    // Start timeout when captcha is loading
    if (!isReady && !timeoutRef.current) {
      timeoutRef.current = setTimeout(() => {
        if (!timeoutLoggedRef.current) {
          timeoutLoggedRef.current = true;
          console.error('Captcha verification timeout', {
            context: 'useCaptcha',
            timeout_ms: CAPTCHA_TIMEOUT_MS,
            message: 'Captcha failed to resolve within timeout period',
          });
        }
      }, CAPTCHA_TIMEOUT_MS);
    }

    // Clear timeout when captcha resolves
    if (isReady && timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [siteKey, isReady]);

  return useMemo(
    () => ({
      /** The current captcha token */
      token,
      /** Whether the captcha is ready (not configured or token available) */
      isReady,
      /** Error code if captcha verification failed */
      error,
      /** Reset the captcha (clears token and resets widget) */
      reset,
      /** The captcha field component to render */
      field,
    }),
    [token, isReady, error, reset, field],
  );
}
